import { formatCPF } from '@/utils/formatters';
import { formatBirthDateBR } from '@/utils/birthDate';
import type { Patient } from '@/types/models';

/**
 * CPF do paciente formatado pra exibição (XXX.XXX.XXX-XX) ou `null`.
 *
 * Usa `patientNumber` de propósito — NÃO use `p.cpf` aqui: o campo `cpf` é
 * gravado criptografado no Firestore (`encryptData`), então num resultado de
 * busca ele vem cifrado e inútil pra mostrar. O `patientNumber` guarda o CPF
 * de 11 dígitos em claro quando o paciente tem CPF (senão fica 'P-######').
 */
export function patientCpfDisplay(p: Patient): string | null {
  const pn = p.patientNumber || '';
  if (/^\d{11}$/.test(pn)) return formatCPF(pn);
  return null;
}

/**
 * Rótulo do CPF do paciente para os seletores: `CPF 123.456.789-00`
 * (ou `Sem CPF` quando não há). Use junto com {@link patientBirthLine} quando
 * quiser CPF e nascimento em linhas separadas (ver `PatientMetaLines`).
 */
export function patientCpfLine(p: Patient): string {
  const cpf = patientCpfDisplay(p);
  return cpf ? `CPF ${cpf}` : 'Sem CPF';
}

/**
 * Rótulo do nascimento do paciente: `Nasc. 18/07/1957`
 * (ou `Nasc. não informada` quando falta a data).
 */
export function patientBirthLine(p: Patient): string {
  const birth = formatBirthDateBR(p.birthDate);
  return birth ? `Nasc. ${birth}` : 'Nasc. não informada';
}

/**
 * Linha padronizada de identificação do paciente para dropdowns de busca e
 * seleção: `CPF 123.456.789-00 · Nasc. 18/07/1957`. Mostra os dois rótulos
 * sempre (com fallback claro quando faltam) para que todos os seletores de
 * paciente exibam a mesma informação — nome (no título) + CPF + nascimento.
 *
 * Para a versão empilhada (CPF e nascimento em linhas próprias, sem corte em
 * dropdowns estreitos), prefira o componente `PatientMetaLines`.
 */
export function patientMetaLine(p: Patient): string {
  return `${patientCpfLine(p)} · ${patientBirthLine(p)}`;
}
